import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { unreadableRowsNotice } from './journeyPresentation';

/**
 * The three things the project list can say when it has no rows to show, kept
 * apart on screen the way the presenter keeps them apart in code.
 *
 * These import no radix primitive, so every CLAIM the list makes is renderable
 * in a Jest test and assertable against real markup rather than against a mock.
 *
 * ACCESSIBILITY. Each state is a `role="status"` region with an `aria-live`
 * announcement, because a screen-reader user who triggers a retry gets no
 * other signal that the answer changed. The heading is a real `<h2>`, the
 * state is carried in `data-state` rather than by colour, and the retry
 * control has an explicit accessible name naming what will be retried.
 */

const StateShell = ({ testId, state, heading, detail, children }) => (
  <Card className="stat-card" data-testid={testId} data-state={state}>
    <CardContent className="p-10 text-center space-y-3" role="status" aria-live="polite">
      <h2 className="text-lg font-semibold">{heading}</h2>
      <p className="text-muted-foreground max-w-xl mx-auto">{detail}</p>
      {children}
    </CardContent>
  </Card>
);

/**
 * The request did not complete.
 *
 * Deliberately carries NO create-a-project control and none of the vocabulary
 * of an empty organisation. We do not know what exists, and the previous
 * screen said "No projects found — Get started by creating your first project"
 * to a contractor who might have forty.
 */
export const ProjectsRefused = ({ view, onRetry, testId = 'projects-refused' }) => (
  <StateShell testId={testId} state={view.kind} heading={view.headline} detail={view.detail}>
    {onRetry ? (
      <button
        type="button"
        onClick={onRetry}
        className="mt-2 rounded-md border px-4 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        data-testid={`${testId}-retry`}
        aria-label="Try loading the project list again"
      >
        Try again
      </button>
    ) : null}
  </StateShell>
);

/**
 * The list was read and it is genuinely empty.
 *
 * This is the ONLY state in which inviting someone to create their first
 * project is a true sentence.
 */
export const ProjectsEmpty = ({ view, onCreate, testId = 'projects-empty' }) => (
  <StateShell testId={testId} state={view.kind} heading={view.headline} detail={view.detail}>
    {onCreate ? (
      <button
        type="button"
        onClick={onCreate}
        className="mt-2 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        data-testid={`${testId}-create`}
        aria-label="Create the first project"
      >
        Create project
      </button>
    ) : null}
  </StateShell>
);

/**
 * Rows came back that the list cannot render.
 *
 * Shown ALONGSIDE the rows that did read, never instead of them: a partial
 * answer is still an answer, and hiding the shortfall is how "12 projects"
 * silently becomes the truth when the backend sent 13.
 */
export const UnreadableRowsNotice = ({ read, testId = 'projects-unreadable-rows' }) => {
  const notice = unreadableRowsNotice(read);
  if (!notice) return null;
  return (
    <p
      className="text-sm text-muted-foreground border-l-2 border-amber-500 pl-3 py-1"
      data-testid={testId}
      role="status"
      aria-live="polite"
    >
      {notice}
    </p>
  );
};

/**
 * A count that may not be established.
 *
 * Used for the work-breakdown tab label, where `({activities.length})` printed
 * "(0)" for a read that failed. A count nobody obtained is not zero, and the
 * label simply carries no number rather than inventing one.
 */
/**
 * `read` covers a collection whose count IS its length. `count` covers a
 * figure the backend states separately — the lookahead total, which is not
 * derivable from the rows on screen. Either way an unestablished count
 * renders as (—) and never as (0).
 */
export const CountBadge = ({ read, count, testId }) => {
  if (count !== undefined) {
    return count === null ? (
      <span className="text-muted-foreground" data-testid={testId} data-established="false" title="Not established">
        (—)
      </span>
    ) : (
      <span data-testid={testId} data-established="true">
        ({count})
      </span>
    );
  }
  const established = Boolean(read) && read.state === 'READ';
  if (!established) {
    return (
      <span className="text-muted-foreground" data-testid={testId} data-established="false" title="Not established">
        (—)
      </span>
    );
  }
  return (
    <span data-testid={testId} data-established="true">
      ({read.items.length})
    </span>
  );
};
